import { useCallback, useEffect, useRef, useState } from "react";
import { createParcelReminderState, parcelReminderReducer, type ParcelReminderEvent } from "../logic/parcelReminderMachine";

export function useParcelReminderDemo(active: boolean, onSpeak: (text: string) => unknown, onStop: () => void) {
  const [model, setModel] = useState(createParcelReminderState);
  const [revision, setRevision] = useState(0);
  const spokenSequence = useRef(0);
  const previousTtsText = useRef("");
  const onEvent = useCallback((event: ParcelReminderEvent) => {
    setModel((current) => parcelReminderReducer(current, event));
  }, []);

  useEffect(() => {
    if (!active || model.phase !== "querying") return;
    const queryToken = model.queryToken;
    const timer = window.setTimeout(() => onEvent({ type: "QUERY_COMPLETE", queryToken }), 650);
    return () => window.clearTimeout(timer);
  }, [active, model.phase, model.queryToken, onEvent, revision]);

  useEffect(() => {
    if (!active || model.surface !== "card") return;
    const timer = window.setTimeout(() => onEvent({ type: "COLLAPSE" }), 15000);
    return () => window.clearTimeout(timer);
  }, [active, model.surface, model.mode, model.queryToken, onEvent, revision]);

  useEffect(() => {
    if (!active || model.ttsSequence === spokenSequence.current) return;
    if (!model.ttsText) {
      spokenSequence.current = model.ttsSequence;
      return;
    }
    // Let StrictMode finish its setup/cleanup cycle before starting playback.
    const timer = window.setTimeout(() => {
      spokenSequence.current = model.ttsSequence;
      onSpeak(model.ttsText);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [active, model.ttsSequence, model.ttsText, onSpeak, revision]);

  useEffect(() => {
    const clearedSpeech = Boolean(previousTtsText.current && !model.ttsText);
    previousTtsText.current = model.ttsText;
    if (active && clearedSpeech) onStop();
  }, [active, model.queryToken, model.ttsText, onStop]);

  const replay = useCallback(() => {
    if (!active) return;
    const text = model.ttsText || (model.pendingCount > 0
      ? model.mode === "manual"
        ? `你有 ${model.pendingCount} 件待取快递。`
        : `${model.poi === "home" ? "家" : "公司"}附近有 ${model.pendingCount} 件待取快递。`
      : model.inTransitCount > 0
        ? `你有 ${model.inTransitCount} 件快递正在运输中，暂时没有待取快递。`
        : "你目前没有待取或运输中的快递。");
    onStop();
    previousTtsText.current = text;
    return onSpeak(text);
  }, [active, model.inTransitCount, model.mode, model.pendingCount, model.poi, model.ttsText, onSpeak, onStop]);

  const reset = useCallback(() => {
    if (active) onStop();
    spokenSequence.current = 0;
    previousTtsText.current = "";
    setModel(createParcelReminderState());
    setRevision((current) => current + 1);
  }, [active, onStop]);

  return { model, onEvent, reset, replay };
}
